
import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  TextField,
  IconButton,
  Tooltip,
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Switch,
  alpha,
  useTheme,
  Container,
  Paper,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import {
  AddCircle as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import axios from "axios";
import PincodeStats from "./components/PincodeStats";
import ToggleButton from "./components/ToggleButton";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  maxWidth: 400,
  width: "90%",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  padding: 3,
  transform: "translate(-50%, -50%)",
};

const deleteModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  maxWidth: 500,
  width: "90%",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  padding: 4,
  height: 180,
  overflowY: "auto",
  transform: "translate(-50%, -50%)",
};

const PincodeSrc = () => {
  const theme = useTheme();
   const navigate = useNavigate()
const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const medstownColors = {
    primary: "#014D4D",
    secondary: "#02A0A0",
    accent: "#FFB800",
    success: "#4caf50",
    error: "#f44336",
    warning: "#ff9800",
  };

  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [newPincode, setNewPincode] = useState("");
  const [newFullName, setNewFullName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newUserId, setNewUserId] = useState("");
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPincodeRow, setSelectedPincodeRow] = useState(null);
  const [switchState, setSwitchState] = useState(() => {
    const savedState = localStorage.getItem("switchState");
    return savedState ? JSON.parse(savedState) : {};
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchPincodes();
  }, [type]);

  useEffect(() => {
    // Synchronize switch state with localStorage whenever data changes
    const savedState = localStorage.getItem("switchState");
    if (savedState) {
      setSwitchState(JSON.parse(savedState));
    }
  }, [data]);

  useEffect(() => {
    const filtered = data.filter((row) =>
      Object.values(row).some(
        (value) =>
          value &&
          value.toString().toLowerCase().includes(searchText.toLowerCase())
      )
    );
    setFilteredRows(filtered);
  }, [searchText, data]);

  const fetchPincodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `https://api.medstown.com/customer/getpincode?type=${type}`
      );
      setData(res?.data.reverse() || []);
    } catch (err) {
      setError("Failed to fetch pincodes");
      toast.error("Failed to fetch pincodes");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleToggleChange = async (state, row) => {
    try {
      const requestData = {
        areaPincode: row?.areaPincode,
        newUserPincode: row?.newUserPincode,
        status: state,
      };
      const res = await axios.post(
        "https://api.medstown.com/customer/pincode/toggle",
        requestData
      );
      setSwitchState((prev) => {
        const updated = { ...prev, [row.newUserPincode]: res.data.isActive };
        localStorage.setItem("switchState", JSON.stringify(updated));
        return updated;
      });
      toast.success(`Pincode ${row.newUserPincode} status updated.`);
    } catch (error) {
      setSwitchState((prevState) => {
        const updatedState = { ...prevState, [row.newUserPincode]: !state };
        localStorage.setItem("switchState", JSON.stringify(updatedState));
        return updatedState;
      });
      toast.error("Error updating pincode status");
    }
  };

  const handleDeletePincode = async () => {
    if (!selectedPincodeRow) return;
    try {
      await axios.delete("https://api.medstown.com/customer/delete/pincode", {
        data: {
          areaPincode: selectedPincodeRow.areaPincode,
          newUserPincode: selectedPincodeRow.newUserPincode,
        },
      });
      setSnackbar({
        open: true,
        message: "Pincode deleted successfully.",
        severity: "success",
      });
      fetchPincodes();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete pincode.",
        severity: "error",
      });
    } finally {
      setDeleteOpen(false);
      setSelectedPincodeRow(null);
    }
  };

  const handleDownload = () => {
    window.location.href =
      "https://api.medstown.com/admin/pincodes/download-csv";
  };

  const handleAddPincode = async () => {
    if (!/^\d{6}$/.test(newPincode)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid 6-digit pincode.",
        severity: "warning",
      });
      return;
    }
    try {
      const response = await axios.post(
        "https://api.medstown.com/customer/addpincode",
        {
          areaPincode: newPincode,
          fullName: newFullName,
          phone: newPhone,
          userId: newUserId,
        }
      );
      if (response.data) {
        setSwitchState((prev) => {
          const updated = { ...prev, [newPincode]: true };
          localStorage.setItem("switchState", JSON.stringify(updated));
          return updated;
        });
        setSnackbar({
          open: true,
          message: "Pincode added successfully!",
          severity: "success",
        });
        setNewPincode("");
        setNewFullName("");
        setNewPhone("");
        setNewUserId("");
        fetchPincodes();
        setOpenModal(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to add pincode.",
        severity: "error",
      });
    }
  };

  const columns = [
    {
      field: "fullName",
      headerName: "Requested By",
      width: 160,
      flex: 1,
      minWidth: 140,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      width: 150,
      flex: 1,
      minWidth: 130,
    },
    {
      field: "areaPincode",
      headerName: "Active Pin-code",
      width: 140,
      flex: 1,
      minWidth: 120,
    },
    {
      field: "newUserPincode",
      headerName: "Requested Pin-Code",
      width: 160,
      flex: 1,
      minWidth: 140,
    },
    {
      field: "createdAt",
      headerName: "Date & Time",
      width: 170,
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => {
        const date = new Date(params.row.createdAt);
        return `${date.toLocaleDateString()} - ${date.toLocaleTimeString()}`;
      },
    },
    {
      field: "switch",
      headerName: "Active",
      width: 100,
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Switch
          color="success"
          checked={switchState[params.row.newUserPincode] || false}
          onChange={(e) => handleToggleChange(e.target.checked, params.row)}
        />
      ),
      align: "center",
    },
    {
      field: "actions",
      headerName: "Delete",
      width: 100,
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Delete Pincode">
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setSelectedPincodeRow(params.row);
              setDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
      align: "center",
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 1, sm: 3 } }}>
      {/* Header */}
      {!isMobile ? (
  <Paper
    elevation={0}
    sx={{
      p: 1,
      mb: 1.5,
      borderRadius: 2,
      background: `linear-gradient(135deg, ${medstownColors.primary}, ${medstownColors.secondary})`,
      color: "white",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      <AddIcon fontSize="small" />
      <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{ 
          letterSpacing: "-0.2px",
          fontSize: "16px"
        }}
      >
        Pincode Requests
      </Typography>
      <Typography
        variant="caption"
        sx={{ 
          opacity: 0.75, 
          fontWeight: 400, 
          flexGrow: 1,
          fontSize: "12px"
        }}
      >
        Manage active and requested pincodes
      </Typography>
      
      <Button
        size="small"
        onClick={() => setOpenModal(true)}
        sx={{
          color: "white",
          fontSize: "11px",
          fontWeight: 500,
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          textTransform: "none",
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.25)"
          }
        }}
      >
        + Add
      </Button>

      <Button
        size="small"
        onClick={() => navigate("/admin/requested-localities")}
        sx={{
          color: "white",
          fontSize: "11px",
          fontWeight: 500,
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          textTransform: "none",
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.3)"
          }
        }}
      >
       Requests Locations
      </Button>
    </Stack>
  </Paper>
) : (
  <Paper
    elevation={0}
    sx={{
      p: 1,
      mb: 1.5,
      borderRadius: 2,
      background: `linear-gradient(135deg, ${medstownColors.primary}, ${medstownColors.secondary})`,
      color: "white",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <AddIcon fontSize="small" />
        <Typography
          variant="subtitle2"
          fontWeight={500}
          sx={{ 
            fontSize: "13px",
            letterSpacing: "-0.1px"
          }}
        >
          Pincode Requests
        </Typography>
      </Stack>
      
      <Button
        size="small"
        onClick={() => setOpenModal(true)}
        sx={{
          color: "white",
          fontSize: "10px",
          fontWeight: 500,
          px: 1,
          py: 0.3,
          borderRadius: 0.5,
          textTransform: "none",
          minWidth: "auto",
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.3)"
          }
        }}
      >
        + Add
      </Button>
      <Button
        size="small"
        onClick={() => navigate("/admin/requested-localities")}
        sx={{
          color: "white",
         
          fontSize: "10px",
          fontWeight: 500,
          px: 1,
          py: 0.3,
          borderRadius: 0.5,
          textTransform: "none",
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.3)"
          }
        }}
      >
       Requests Locations
      </Button>
    </Stack>
  </Paper>
)}

      {/* Stats Component */}
      <Box sx={{ mt: 4 }}>
        <PincodeStats setType={setType} type={type} />
      </Box>
      {/* Search */}
      <TextField
        variant="outlined"
        size="small"
        placeholder="Search pincodes, requesters ..."
        fullWidth
        value={searchText}
        onChange={handleSearchTextChange}
        InputProps={{
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: medstownColors.primary }} />
          ),
          endAdornment: searchText && (
            <IconButton
              onClick={() => setSearchText("")}
              size="small"
              sx={{ color: medstownColors.primary }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
        sx={{
          mb: 2,
          mt:3,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
          fontSize: "0.85rem",
        }}
      />

      {/* DataGrid */}
      <Paper
        elevation={1}
        sx={{
          width: "100%",
          overflow: "hidden",
          borderRadius: 3,
          height: 520,
          "& .MuiDataGrid-root": {
            border: "none",
            fontSize: "0.85rem",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: alpha(medstownColors.primary, 0.1),
            color: medstownColors.primary,
            fontWeight: 700,
            fontSize: "0.9rem",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha(medstownColors.secondary, 0.1),
            cursor: "pointer",
          },
        }}
      >
        {loading ? (
          <Stack
            height="100%"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading pincodes...
            </Typography>
          </Stack>
        ) : (
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            getRowId={(row) => row._id}
            components={{
              Toolbar: GridToolbar,
            }}
            onRowClick={(params) => {
              // Optional: Click to show more details or edit
            }}
          />
        )}
      </Paper>

      {/* Add Pincode Modal */}
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        closeAfterTransition
      >
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2} fontWeight={700}>
            Add New Pincode
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="New Pincode"
              variant="outlined"
              size="small"
              fullWidth
              value={newPincode}
              onChange={(e) => setNewPincode(e.target.value)}
              inputProps={{ maxLength: 6 }}
            />
            <TextField
              label="Full Name"
              variant="outlined"
              size="small"
              fullWidth
              value={newFullName}
              onChange={(e) => setNewFullName(e.target.value)}
            />
            <TextField
              label="Phone"
              variant="outlined"
              size="small"
              fullWidth
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
            />
            <TextField
              label="User Id"
              variant="outlined"
              size="small"
              fullWidth
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={2} mt={1}>
              <Button onClick={() => setOpenModal(false)} variant="outlined">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddPincode}
              >
                Add
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedPincodeRow(null);
        }}
      >
        <Box sx={deleteModalStyle}>
          <Typography variant="h6" fontWeight={700} mb={1}>
            Delete Pincode
          </Typography>
          <Typography>
            Are you sure you want to delete the pincode{" "}
            <b>{selectedPincodeRow?.newUserPincode}</b>? This action cannot be
            undone.
          </Typography>

          <Stack direction="row" justifyContent="flex-end" spacing={2} mt={4}>
            <Button
              variant="outlined"
              onClick={() => {
                setDeleteOpen(false);
                setSelectedPincodeRow(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeletePincode}
            >
              Delete
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PincodeSrc;
